import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel, MaskedLine } from "@/components/Reveal";
import { EditorialMarquee, LogoNavy } from "@/components/Layout";
import { WPP_URL } from "@/links";

const CHAPTERS = [
  {
    era: "Primeiros 5 anos",
    t: "Fuchs Comunicação Estratégica",
    d: "A construção de marcas por meio de uma comunicação mais eficiente e eficaz com o público.",
  },
  {
    era: "Segundo capítulo",
    t: "Fuchs Inteligência Digital",
    d: "Ampliamos o olhar para o digital e para a necessidade de entender melhor as ferramentas e o público antes de comunicar e vender.",
  },
  {
    era: "Hoje",
    t: "FUCHS | Posicionamento e Direção de Marcas",
    d: "Atuamos nas decisões que dão clareza, posicionamento e direção ao crescimento.",
  },
];

const PRINCIPLES = [
  {
    t: "O primeiro case é a própria Fuchs",
    d: "Antes de lançar a Plataforma 4D para clientes, aplicamos o método em nossa evolução. O reposicionamento da Fuchs é o primeiro case que conduzimos.",
  },
  {
    t: "Cognição antes da estética",
    d: "Diagnóstico e direção vêm antes do design. Nenhuma identidade é desenhada sem posicionamento comprovado.",
  },
  {
    t: "IA e juízo humano",
    d: "Os agentes da Fuchs cuidam da tabulação e das correlações. As decisões estratégicas são de gente.",
  },
];

export default function QuemSomos() {
  return (
    <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <section data-testid="about-hero" className="hero-glow noise relative overflow-hidden bg-ink pb-24 pt-36 lg:pb-32 lg:pt-48">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <SectionLabel>Quem somos</SectionLabel>
          </motion.div>
          <h1 className="mt-8 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>15 anos de experiência.</MaskedLine>
            <MaskedLine delay={0.4}>
              Um <span className="font-serif font-medium italic text-brand-orange">novo nível</span> de pensamento.
            </MaskedLine>
          </h1>
          <motion.p
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            A Fuchs nasceu para ajudar marcas a se comunicarem melhor com seus públicos.
            O mercado mudou, os negócios amadureceram e o nosso olhar também.
          </motion.p>
        </div>
      </section>

      <EditorialMarquee />

      <section data-testid="about-evolution" className="bg-ink py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden border border-white/10">
              <img
                src="/assets/evolucao.jpg"
                alt="Evolução da marca Fuchs: Comunicação Estratégica, depois Inteligência Digital e, hoje, Posicionamento e Direção de Marcas"
                data-testid="evolution-image"
                className="max-h-[440px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
            </div>
          </Reveal>

          <div className="mt-20">
            <Reveal>
              <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Três capítulos, uma mesma trajetória.
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-10 lg:grid-cols-3">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.t} delay={i * 0.1}>
                  <div
                    data-testid={`chapter-card-${i + 1}`}
                    className="group h-full border-l-2 border-white/15 pl-7 transition-colors duration-500 hover:border-brand-orange"
                  >
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-orange">{c.era}</span>
                    <h3 className="mt-4 font-display text-xl font-bold leading-snug text-white sm:text-2xl">{c.t}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal className="mt-24 border-y border-white/10 py-14">
            <div className="flex flex-wrap items-baseline justify-center gap-x-8 gap-y-4 text-center">
              {["falar", "pensar", "decidir"].map((w, i) => (
                <span key={w} className="flex items-baseline gap-8">
                  <span
                    data-testid={`evolution-word-${w}`}
                    className={`font-display text-5xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl ${
                      i === 2 ? "text-brand-orange" : "text-outline"
                    }`}
                  >
                    {w}
                  </span>
                  {i < 2 && <span className="hidden h-2 w-2 rounded-full bg-brand-magenta sm:block" />}
                </span>
              ))}
            </div>
            <p className="mx-auto mt-8 max-w-2xl text-center font-serif text-lg italic leading-relaxed text-slate-400">
              Não é uma ruptura. É a evolução natural de uma trajetória que passou por falar,
              pensar e, agora, decidir.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-danielle" className="bg-paper py-24 text-navy lg:py-32">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-[480px]">
              <div aria-hidden className="absolute -left-4 -top-4 h-full w-full border border-brand-orange/50" />
              <img
                src="/assets/danielle-portrait.jpg"
                alt="Danielle Fuchs sentada em poltrona vermelha, de blazer azul-marinho"
                data-testid="about-portrait"
                className="relative aspect-[2/3] w-full object-cover object-center"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <LogoNavy className="mb-8" />
              <SectionLabel light>Danielle Fuchs</SectionLabel>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                A inteligência por trás do método.
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/70">
                Conduz decisões de posicionamento e crescimento ao lado da liderança das empresas.
              </p>
            </Reveal>
            <div className="mt-10 flex gap-12">
              <Reveal delay={0.1}>
                <p className="font-display text-5xl font-extrabold text-brand-orange lg:text-6xl">20+</p>
                <p className="mt-2 max-w-[180px] font-mono text-[11px] uppercase leading-relaxed tracking-[0.15em] text-navy/60">
                  anos de experiência em decisões estratégicas
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="font-display text-5xl font-extrabold text-brand-orange lg:text-6xl">100+</p>
                <p className="mt-2 max-w-[180px] font-mono text-[11px] uppercase leading-relaxed tracking-[0.15em] text-navy/60">
                  empresas orientadas em posicionamento e crescimento
                </p>
              </Reveal>
            </div>
            <div className="mt-14 space-y-8">
              {PRINCIPLES.map((p, i) => (
                <Reveal key={p.t} delay={i * 0.08}>
                  <div data-testid={`principle-${i + 1}`} className="border-l-2 border-navy/15 pl-7 transition-colors duration-500 hover:border-brand-orange">
                    <h3 className="font-display text-lg font-bold sm:text-xl">{p.t}</h3>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-navy/70">{p.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="about-instagram" className="noise relative bg-ink py-24 lg:py-32">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-10">
          <Reveal>
            <SectionLabel>Acompanhe a FUCHS</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Conteúdo sobre posicionamento, marca e crescimento,{" "}
              <span className="font-serif font-medium italic text-brand-magenta">direto no Instagram.</span>
            </h2>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="https://www.instagram.com/fuchs.brand_/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-instagram-cta"
                className="group inline-flex items-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red"
              >
                @fuchs.brand_
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <a
                href={WPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-whatsapp-cta"
                className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
              >
                Fale com a Fuchs
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}
